import { Injectable, inject } from '@angular/core';
import type { CombinationPriceGrid, SaveCombinationPricesRequest } from '@models/combination-prices';
import { TranslateService } from '@ngx-translate/core';
import { CrmAccommodationsService } from '@services/crm-accommodations.service';
import { CrmCombinationPricesService } from '@services/crm-combination-prices.service';
import { CrmFishingSpotsService } from '@services/crm-fishing-spots.service';
import { CrmWaterPeriodsService } from '@services/crm-water-periods.service';
import { CrmWaterPriceListsService } from '@services/crm-water-price-lists.service';
import { forkJoin, map, type Observable, of } from 'rxjs';
import type {
  CombinationPricingInitialData,
  CombinationPricingUnitType,
  CopySource,
  WaterPriceListOption,
} from './combination-pricing.model';

/**
 * Data-access layer for {@link CombinationPricingComponent}: wraps the four CRM services the grid needs,
 * hides the accommodation / fishing-spot / water-raffle endpoint split behind `unitType`, and assembles
 * the one-shot initial load. Purely request-building — error toasts, spinners and state live in the caller.
 */
@Injectable({ providedIn: 'root' })
export class CombinationPricingDataService {
  private readonly combinationService = inject(CrmCombinationPricesService);
  private readonly priceListsService = inject(CrmWaterPriceListsService);
  private readonly periodsService = inject(CrmWaterPeriodsService);
  private readonly fishingSpotsService = inject(CrmFishingSpotsService);
  private readonly accommodationsService = inject(CrmAccommodationsService);
  private readonly translate = inject(TranslateService);

  /** Price lists + dimensions + copy sources for a water, with the unit being edited excluded from sources. */
  loadInitial(
    waterId: number,
    unitType: CombinationPricingUnitType,
    unitId: number,
  ): Observable<CombinationPricingInitialData> {
    // The raffle grid has no copy sources (see buildCopySources), so it does not pay for the two listings
    // they are built from — the lake it runs on has no swims to list in the first place.
    const wantsCopySources = unitType !== 'water';

    return forkJoin({
      priceLists: this.priceListsService.getPriceLists(waterId),
      dimensions: this.periodsService.getPricingDimensions(waterId),
      spots: wantsCopySources ? this.fishingSpotsService.getSpots(waterId) : of([]),
      accommodations: wantsCopySources ? this.accommodationsService.getAccommodations(waterId) : of([]),
    }).pipe(
      map(({ priceLists, dimensions, spots, accommodations }) => ({
        priceLists,
        seasons: dimensions.seasons,
        stayTypes: dimensions.stayTypes,
        copySources: this.buildCopySources(spots, accommodations, unitType, unitId),
      })),
    );
  }

  /**
   * The price lists alone, for adopting a change made in the lake's Price lists section without re-reading
   * the grid the user may be part-way through editing. Only the raffle grid needs this: it is the one that
   * shares a page with that section (see {@link CombinationPricingComponent.priceListsChanged}).
   */
  getPriceLists(waterId: number): Observable<WaterPriceListOption[]> {
    return this.priceListsService.getPriceLists(waterId);
  }

  /** Load the full grid (all seasons × stay types × cells) for one unit's price list. */
  getGrid(
    unitType: CombinationPricingUnitType,
    waterId: number,
    unitId: number,
    priceListId: number,
  ): Observable<CombinationPriceGrid> {
    switch (unitType) {
      case 'fishing-spot':
        return this.combinationService.getFishingSpotGrid(waterId, unitId, priceListId);
      case 'water':
        return this.combinationService.getWaterRaffleGrid(waterId, priceListId);
      default:
        return this.combinationService.getAccommodationGrid(waterId, unitId, priceListId);
    }
  }

  /** PUT the whole grid for one unit's price list (the API reconciles to minimal writes). */
  saveGrid(
    unitType: CombinationPricingUnitType,
    waterId: number,
    unitId: number,
    priceListId: number,
    request: SaveCombinationPricesRequest,
  ): Observable<CombinationPriceGrid> {
    switch (unitType) {
      case 'fishing-spot':
        return this.combinationService.saveFishingSpotGrid(waterId, unitId, priceListId, request);
      case 'water':
        return this.combinationService.saveWaterRaffleGrid(waterId, priceListId, request);
      default:
        return this.combinationService.saveAccommodationGrid(waterId, unitId, priceListId, request);
    }
  }

  /**
   * Other swims + accommodations of this water, minus the unit being edited, as copy sources.
   *
   * The raffle grid gets none. It only exists on a lake with no swims to copy from, and an accommodation's
   * grid prices a lodge rather than rod places — the same (anglers, non-anglers) numbers mean a different
   * thing there, so offering it as a source would invite copying nonsense in.
   */
  private buildCopySources(
    spots: { id: number; number: number | null }[],
    accommodations: { id: number; name: string | null; number: number | null }[],
    unitType: CombinationPricingUnitType,
    unitId: number,
  ): CopySource[] {
    if (unitType === 'water') return [];

    // A swim has no meaningful own name (its title defaults to the water's), so label it by number.
    const spotSources: CopySource[] = spots
      .filter((s) => !(unitType === 'fishing-spot' && s.id === unitId))
      .map((s) => ({
        id: s.id,
        type: 'fishing-spot' as const,
        label: this.translate.instant('waters.fishing-spots.swim-label', {
          number: s.number ?? s.id,
        }),
      }));

    const accommodationSources: CopySource[] = accommodations
      .filter((a) => !(unitType === 'accommodation' && a.id === unitId))
      .map((a) => ({
        id: a.id,
        type: 'accommodation' as const,
        label: this.translate.instant('waters.combination-prices.copy-accommodation-label', {
          name: a.name ?? `#${a.number ?? a.id}`,
        }),
      }));

    return [...accommodationSources, ...spotSources];
  }
}
